// minimal typings to satisfy TimelineChartData from heat-next while keeping this file self contained.
// ideally we'd use imported types from heat-next, but let's not worry too much
type Iso = string;

type ScatterPoint = { Data: number | string; DateTime: Iso };
type AreaPoint = { Data: number | string; DateTime: Iso };
type VerticalPoint = { DateTime: Iso };
type RangeBarPoint = { StartTime: Iso; EndTime: Iso; [k: string]: any };

type BaseDataset = {
    name: string;
    radioLabel?: string;
    color?: string;
    unit?: string;
    type: string;
    groupTitle?: string;
    radioType?: string;
    position?: number;
};

type AreaDataset = BaseDataset & { type: "area"; data: AreaPoint[]; yDomain?: [number, number] };
type ScatterDataset = BaseDataset & { type: "scatter1"; data: ScatterPoint[] };
type VerticalLineDataset = BaseDataset & { type: "verticalLine"; data: VerticalPoint[] };
type RangeBarDataset = BaseDataset & { type: "rangeBar"; data: RangeBarPoint[] };
type UnknownDataset = BaseDataset & { data: any[] };

type Dataset =
    | AreaDataset
    | ScatterDataset
    | VerticalLineDataset
    | RangeBarDataset
    | UnknownDataset;

type LegendItem = {
    text: string;
    color?: string;
    code?: string | null;
    min?: number | null;
    max?: number | null;
};

type TimelineLegend = { items: LegendItem[] };

type GridView = {
    columnPosition?: number;
    rowPosition?: number;
    groupOrder?: number;
    groupSection?: string;
} | null;

export type TimelineChartData = {
    title: string;
    datasets: Dataset[];
    chartType: string;
    gridView: GridView;
    legend?: TimelineLegend;
    code: string;
    description: string;
};

// util: safe parse datetime
function toTime(s: Iso | undefined): number | null {
    if (!s) return null;
    const t = Date.parse(s);

    return Number.isFinite(t) ? t : null;
}

function iso(t: number): Iso {
    return new Date(t).toISOString();
}

// util: round to 1 decimal to avoid fp dust like .0000000001
function round1(n: number): number {
    // avoid locale/precision quirks by working in integers
    return Math.round(n * 10) / 10;
}

// compute global min/max across all charts/datasets
function computeGlobalRange(charts: TimelineChartData[]): { min: number; max: number } | null {
    let min: number | null = null;
    let max: number | null = null;

    for (const chart of charts) {
        for (const ds of chart.datasets ?? []) {
            if (!ds || !Array.isArray((ds as any).data)) continue;

            if (ds.type === "area" || ds.type === "scatter1") {
                for (const p of (ds as any).data as (AreaPoint | ScatterPoint)[]) {
                    const t = toTime((p as any)?.DateTime);

                    if (t == null) continue;
                    min = min == null ? t : Math.min(min, t);
                    max = max == null ? t : Math.max(max, t);
                }
            } else if (ds.type === "verticalLine") {
                for (const p of (ds as VerticalLineDataset).data) {
                    const t = toTime(p?.DateTime);

                    if (t == null) continue;
                    min = min == null ? t : Math.min(min, t);
                    max = max == null ? t : Math.max(max, t);
                }
            } else if (ds.type === "rangeBar") {
                for (const p of (ds as RangeBarDataset).data) {
                    const s = toTime(p?.StartTime);
                    const e = toTime(p?.EndTime);

                    if (s != null) min = min == null ? s : Math.min(min, s);

                    if (e != null) max = max == null ? e : Math.max(max, e);
                }
            } else {
                // unknown types: try DateTime
                for (const p of (ds as UnknownDataset).data) {
                    const t = toTime(p?.DateTime);

                    if (t == null) continue;
                    min = min == null ? t : Math.min(min, t);
                    max = max == null ? t : Math.max(max, t);
                }
            }
        }
    }

    if (min == null || max == null) return null;

    // snap global window to whole seconds for clean 1hz stepping
    const snappedMin = Math.floor(min / 1000) * 1000;
    const snappedMax = Math.ceil(max / 1000) * 1000;

    return { min: snappedMin, max: snappedMax };
}

// fill an area dataset to 1hz over [globalMin, globalMax] with last-known-good values
function densifyAreaDataset1Hz(ds: AreaDataset, globalMin: number, globalMax: number): AreaDataset {
    const raw = Array.isArray(ds.data) ? ds.data : [];

    // normalize + coerce
    const cleaned: { Data: number; _t: number }[] = raw
        .map((p) => {
            const t = toTime(p?.DateTime);
            const val =
                typeof p?.Data === "number" ? p.Data : p?.Data != null ? Number(p.Data) : NaN;

            if (t == null || !Number.isFinite(val)) return null;

            return { Data: round1(val), _t: t };
        })
        .filter((x): x is { Data: number; _t: number } => !!x)
        .sort((a, b) => a._t - b._t);

    if (cleaned.length === 0) {
        return { ...ds, data: [] };
    }

    // dedupe by timestamp (keep last) and clamp timestamps to whole seconds (so we don't chase sub-ms)
    const bySec = new Map<number, number>();

    for (const p of cleaned) {
        const sec = Math.floor(p._t / 1000) * 1000;
        bySec.set(sec, p.Data);
    }

    // carry forward at 1hz across the global window
    const start = Math.floor(globalMin / 1000) * 1000;
    const end = Math.floor(globalMax / 1000) * 1000;

    // seed with first known value at the earliest known timestamp, or at start if data starts later
    // find earliest key in bySec
    const knownTimes = Array.from(bySec.keys()).sort((a, b) => a - b);
    let lastValue: number | null = null;

    // if the first known time is after start, we still need a value; use that first known value
    if (knownTimes.length > 0) {
        lastValue = bySec.get(knownTimes[0]) ?? null;
    }

    const out: { Data: number; DateTime: Iso }[] = [];

    for (let t = start; t <= end; t += 1000) {
        // update lastValue when we hit a known timestamp
        if (bySec.has(t)) {
            lastValue = bySec.get(t)!;
        }

        // if lastValue is still null (no reading yet), keep using the earliest known value
        if (lastValue == null && knownTimes.length > 0) {
            lastValue = bySec.get(knownTimes[0])!;
        }

        // push rounded value at exact second
        out.push({ Data: round1(lastValue ?? 0), DateTime: iso(t) });
    }

    return { ...ds, data: out };
}

// enforce cognitive load axis 0–21 hint
function enforceCognitiveLoadBounds(chart: TimelineChartData): TimelineChartData {
    const isCognitive =
        chart.datasets?.some((d) => d.name === "CognitiveLoad") ||
        chart.legend?.items?.some((i) => i.text === "Cognitive Load");

    if (!isCognitive) return chart;

    if (chart.legend?.items?.length) {
        chart.legend.items = chart.legend.items.map((it) =>
            it.text === "Cognitive Load" ? { ...it, min: 0, max: 21 } : it,
        );
    }

    chart.datasets = chart.datasets.map((d) => {
        if (d.type === "area" && d.name === "CognitiveLoad") {
            const a = d as AreaDataset;

            return { ...a, yDomain: [0, 21] };
        }

        return d;
    });

    return chart;
}

export function sanitizeAndAlignCharts(
    input: TimelineChartData[] | null | undefined,
): TimelineChartData[] {
    if (!Array.isArray(input) || input.length === 0) return input ?? [];

    // deep clone
    const charts: TimelineChartData[] = JSON.parse(JSON.stringify(input));

    const range = computeGlobalRange(charts);

    if (!range) {
        return charts.map(enforceCognitiveLoadBounds);
    }

    const { min: globalMin, max: globalMax } = range;

    for (const chart of charts) {
        if (!chart?.datasets) continue;

        chart.datasets = chart.datasets.map((ds) => {
            if (ds?.type === "area") {
                // fill to 1hz using locf and round values to 1 dp
                return densifyAreaDataset1Hz(ds as AreaDataset, globalMin, globalMax);
            }

            // non-area types unchanged
            return ds;
        });

        enforceCognitiveLoadBounds(chart);
    }

    return charts;
}
